import { useState, useEffect, useRef } from 'react';
import { Volume, VolumeX } from 'lucide-react';
import { toggleMute, isMuted } from '../../../utils/soundService';
import { useGameStore } from '../../../stores/gameStore';

interface TopBarProps {
  title?: string;
}

export function TopBar({ title = 'laura.ai' }: TopBarProps) {
  const [muted, setMuted] = useState(isMuted());
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [gameTime, setGameTime] = useState('03:00');
  const [showDateTooltip, setShowDateTooltip] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dateTooltipRef = useRef<HTMLDivElement>(null);

  const setLoggedIn = useGameStore((state) => state.setLoggedIn);
  const setShowGameStart = useGameStore((state) => state.setShowGameStart);
  const getGameTime = useGameStore((state) => state.getGameTime);
  const gameElapsedMinutes = useGameStore((state) => state.gameElapsedMinutes);

  useEffect(() => {
    const checkMuteStatus = () => setMuted(isMuted());
    const interval = setInterval(checkMuteStatus, 100);
    return () => clearInterval(interval);
  }, []);

  // Update game time display based on elapsed minutes
  useEffect(() => {
    setGameTime(getGameTime());
  }, [gameElapsedMinutes, getGameTime]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleToggleMute = () => {
    toggleMute();
    setMuted(isMuted());
  };

  const handleLogout = () => {
    setLoggedIn(false);
    setDropdownOpen(false);
  };

  const handleLeave = () => {
    setShowGameStart(true);
    setLoggedIn(false);
    setDropdownOpen(false);
  };

  return (
    <div className="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-6">
      <div className="flex items-center gap-8">
        {title && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="hover:text-gray-600 text-gray-400 font-light transition-colors cursor-pointer"
            >
              {title}
            </button>
            {dropdownOpen && (
              <div className="absolute left-0 mt-2 w-40 bg-white border border-gray-200 rounded shadow-lg z-50">
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-200"
                >
                  Log out
                </button>
                <button
                  onClick={handleLeave}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Leave
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-6 text-sm text-gray-400">
        <span className="font-mono">trent.1766@laura.ai</span>
        <button
          onClick={handleToggleMute}
          className="hover:text-gray-600 transition-colors cursor-pointer"
          title={muted ? 'Unmute' : 'Mute'}
        >
          {!muted ? <Volume size={18} /> : <VolumeX size={18} />}
        </button>
        <div
          ref={dateTooltipRef}
          className="relative"
          onMouseEnter={() => setShowDateTooltip(true)}
          onMouseLeave={() => setShowDateTooltip(false)}
        >
          <span className="hover:text-gray-600 transition-colors cursor-pointer font-mono">
            {gameTime}
          </span>
          {showDateTooltip && (
            <div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded shadow-lg z-50">
              <div className="px-2 py-2 text-xs text-gray-400 whitespace-nowrap text-center">
                Jan-30-2030
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
